import { assign, fromPromise, setup } from "xstate"
import {
  AvailableDate,
  AvailableTime,
  fetchAvailableDates,
  fetchAvailableTimes
} from "#/features/telemedicine/booking/mockBookingData"

type BookingMachineContext = {
  selectedDate: string
  paymentMethod: string
  availableDates: AvailableDate[]
  availabilityError: boolean
  availableTimes: AvailableTime[]
  timesError: boolean
}

export type BookingMachineEvent =
  | { type: "SEE_AVAILABILITY" }
  | { type: "DATE_CHOSEN"; date: string }
  | { type: "TIME_CHOSEN"; paymentMethod: string }
  | { type: "BACK" }
  | { type: "CONTINUE" }

// Form values live entirely in the one RHF instance created in BookingForm.tsx —
// this machine only tracks navigation and the two async lookups' lifecycles.
// selectedDate/paymentMethod below are machine bookkeeping (routing an actor's
// input, picking a guard branch), not a copy of user-facing form data.
export const bookingMachine = setup({
  types: {
    context: {} as BookingMachineContext,
    events: {} as BookingMachineEvent
  },
  actors: {
    fetchAvailableDates: fromPromise(fetchAvailableDates),
    fetchAvailableTimes: fromPromise(({ input }: { input: string }) => fetchAvailableTimes(input))
  },
  guards: {
    isInsurance: ({ context }) => context.paymentMethod === "insurance"
  }
}).createMachine({
  id: "booking",
  initial: "enteringDetails",
  context: {
    selectedDate: "",
    paymentMethod: "",
    availableDates: [],
    availabilityError: false,
    availableTimes: [],
    timesError: false
  },
  states: {
    enteringDetails: {
      on: { SEE_AVAILABILITY: "loadingAvailability" }
    },
    loadingAvailability: {
      entry: assign({ availabilityError: false }),
      invoke: {
        src: "fetchAvailableDates",
        onDone: {
          target: "choosingDateTime",
          actions: assign({ availableDates: ({ event }) => event.output })
        },
        onError: {
          target: "enteringDetails",
          actions: assign({ availabilityError: true })
        }
      }
    },
    // A compound state rather than inferring "which sub-step" from a watched form
    // field, so each phase (date/times-loading/time) is independently testable.
    choosingDateTime: {
      initial: "choosingDate",
      on: { BACK: "enteringDetails" },
      states: {
        choosingDate: {
          on: {
            DATE_CHOSEN: {
              target: "loadingTimes",
              actions: assign({ selectedDate: ({ event }) => event.date })
            }
          }
        },
        loadingTimes: {
          entry: assign({ timesError: false }),
          invoke: {
            src: "fetchAvailableTimes",
            input: ({ context }) => context.selectedDate,
            onDone: {
              target: "choosingTime",
              actions: assign({ availableTimes: ({ event }) => event.output })
            },
            onError: {
              target: "choosingDate",
              actions: assign({ timesError: true })
            }
          }
        },
        choosingTime: {
          on: {
            BACK: "choosingDate",
            TIME_CHOSEN: {
              target: "#confirming",
              actions: assign({ paymentMethod: ({ event }) => event.paymentMethod })
            }
          }
        }
      }
    },
    // Transient — the two branches are separate states/components so insurance and
    // payment don't end up sharing a render just because both follow "confirming."
    confirming: {
      id: "confirming",
      always: [{ target: "collectingInsurance", guard: "isInsurance" }, { target: "collectingPayment" }]
    },
    collectingInsurance: {
      on: { BACK: "choosingDateTime.choosingTime", CONTINUE: "complete" }
    },
    collectingPayment: {
      on: { BACK: "choosingDateTime.choosingTime", CONTINUE: "complete" }
    },
    complete: { type: "final" }
  }
})
